<script lang="ts" context="module">
  export const schema = cloneDeep(RichTextSchema);

  // Fix links href to sanitize url
  schema.marks.link = (node) => {
    /* eslint-disable @typescript-eslint/no-unused-vars */
    const { story, uuid, linktype = 'url', ...attrs } = node.attrs;

    attrs.class =
      'link  [&>*]:!text-accent hover:[&>*]:!text-accent/80 visited:[&>*]:!text-accent/60';

    if (linktype === 'email') {
      attrs.href = `mailto:${attrs.href}`;
    }

    if (attrs.href && linktype === 'story') {
      attrs.href = attrs.href.replace('pages/', '');
    }

    if (attrs.anchor) {
      attrs.href = `${attrs.href}#${attrs.anchor}`;
      delete attrs.anchor;
    }

    if (attrs.custom) {
      for (const key in attrs.custom) {
        attrs[key] = attrs.custom[key];
      }
      delete attrs.custom;
    }

    return {
      tag: [{ tag: 'a', attrs: attrs }]
    };
  };

  export const resolver = new RichTextResolver(schema);

  export const hasRichTextContent = (doc: ISbRichtext | undefined) => {
    return (
      doc &&
      doc.type === 'doc' &&
      doc.content &&
      doc.content.length > 0 &&
      // storyblok sometimes sends just an empty paragraph
      !(doc.content.length === 1 && doc.content[0].type === 'paragraph' && !doc.content[0].content)
    );
  };
</script>

<script lang="ts">
  import cloneDeep from 'clone-deep';
  import { RichTextResolver, type ISbRichtext, RichTextSchema } from '@storyblok/js';

  import DynamicRichTextBlock from './rich-text-blocks/dynamic-rich-text-block.svelte';

  import { cn } from '$lib/utils';

  import type { HTMLAttributes } from 'svelte/elements';
  import type { ComponentProps } from 'svelte';

  type $$Props = HTMLAttributes<HTMLDivElement> & {
    doc: ISbRichtext;
    as?: string;
    richTextBlockProps?: ComponentProps<DynamicRichTextBlock>['richTextBlockProps'];
  };

  export let doc: $$Props['doc'];
  let className: $$Props['class'] = undefined;
  export { className as class };
  export let as: $$Props['as'] = 'div';

  // TODO: improve this approach to prevent excess props being passed
  export let richTextBlockProps: $$Props['richTextBlockProps'] = undefined;
</script>

{#if doc.type === 'doc' && doc.content?.length}
  <svelte:element this={as} class={cn('richtext', className)} {...$$restProps}>
    {#each doc.content as section}
      {#if section.type === 'paragraph' && section.content?.length}
        <!-- eslint-disable-next-line svelte/no-at-html-tags -->
        <p style="line-height: 22px;">{@html resolver.render(section)}</p>
      {:else if section.type === 'heading' && section.content?.length}
        <svelte:element this={`h${section.attrs.level}`}>
          <!-- eslint-disable-next-line svelte/no-at-html-tags -->
          {@html resolver.render(section)}
        </svelte:element>
      {:else if section.type === 'blockquote' && section.content?.length}
        <blockquote>
          <!-- eslint-disable-next-line svelte/no-at-html-tags -->
          {@html resolver.render(section)}
        </blockquote>
      {:else if section.type === 'ordered_list' && section.content?.length}
        <!-- eslint-disable-next-line svelte/no-at-html-tags -->
        <ol>{@html resolver.render(section)}</ol>
      {:else if section.type === 'bullet_list' && section.content?.length}
        <!-- eslint-disable-next-line svelte/no-at-html-tags -->
        <ul>{@html resolver.render(section)}</ul>
      {:else if section.type === 'blok'}
        {#each section.attrs.body as block}
          <DynamicRichTextBlock {block} {...richTextBlockProps} />
        {/each}
      {/if}
    {/each}
  </svelte:element>
{/if}
